import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, gbp, Loading } from "@/components/ui-bits";
import { Hammer, Users, Plus, X, Loader2, Trash2, Phone, Mail, Search, ChevronRight, MapPin, FileText, Check, Workflow as WorkflowIcon } from "lucide-react";
import { toast } from "sonner";
import TeamInbox from "@/components/TeamInbox";
import ProjectThread from "@/components/ProjectThread";
import { WorkflowTracker } from "@/components/WorkflowTracker";

const LL_STATUSES = ["In Progress", "Awaiting Contractor Approval", "Awaiting Funds from Landlord", "On Hold", "Completed"];
const SUGGESTED_STAGES = [
  { name: "Survey & scope of works", checklist: ["Site visit booked", "Scope agreed with landlord"] },
  { name: "Budget & quotes", checklist: ["Quotes requested", "Budget approved"] },
  { name: "Strip out & clearance", checklist: ["Skip ordered", "Site cleared"] },
  { name: "Structural / damp works", checklist: [] },
  { name: "First fix (electrics & plumbing)", checklist: ["Electrics first fix", "Plumbing first fix"] },
  { name: "Plastering", checklist: [] },
  { name: "Second fix (electrics & plumbing)", checklist: [] },
  { name: "Kitchen installation", checklist: [] },
  { name: "Bathroom installation", checklist: [] },
  { name: "Flooring", checklist: [] },
  { name: "Decorating", checklist: [] },
  { name: "Snagging & final clean", checklist: ["Snag list completed", "Professional clean"] },
  { name: "Compliance certs (EPC / Gas / EICR)", checklist: ["EPC", "Gas safety", "EICR"] },
  { name: "Final inspection & sign-off", checklist: [] },
];
const SUGGESTED_NAMES = new Set(SUGGESTED_STAGES.map((s) => s.name));
const emptyStage = () => ({ name: "", assignee: "", sop_text: "", checklist: [""] });
const initialForm = () => ({ type: "Refurbishment", property_ref: "", contractor_ids: [], stages: [] });

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [newProj, setNewProj] = useState(false);
  const [addCon, setAddCon] = useState(false);
  const [pForm, setPForm] = useState(initialForm());
  const [cForm, setCForm] = useState({ name: "", phone: "", email: "", trade: "", areas: "" });
  const [busy, setBusy] = useState(false);
  const [conExpanded, setConExpanded] = useState(false);
  const [conQ, setConQ] = useState("");
  const [wfModal, setWfModal] = useState(null);
  const [staff, setStaff] = useState([]);

  const loadProjects = () => api.get("/projects").then(({ data }) => setProjects(data));
  const loadContractors = () => api.get("/contractors").then(({ data }) => setContractors(data));
  const loadProperties = () => api.get("/properties").then(({ data }) => setProperties(Array.isArray(data) ? data : (data.properties || [])));
  const loadStaff = () => api.get("/team-members").then(({ data }) => setStaff(data)).catch(() => {});
  useEffect(() => { loadProjects(); loadContractors(); loadProperties(); loadStaff(); }, []);
  if (!projects) return <Layout title="Projects"><Loading /></Layout>;

  const setStatus = async (proj, value) => {
    setProjects((ps) => ps.map((p) => p.project_id === proj.project_id ? { ...p, landlord_status: value } : p));
    try { await api.patch(`/projects/${proj.project_id}`, { landlord_status: value }); toast.success("Landlord status updated"); }
    catch { toast.error("Could not update status"); }
  };

  // ---- stage builder helpers ----
  const updateStage = (idx, key, val) => setPForm((f) => ({ ...f, stages: f.stages.map((s, i) => i === idx ? { ...s, [key]: val } : s) }));
  const addStage = () => setPForm((f) => ({ ...f, stages: [...f.stages, emptyStage()] }));
  const removeStage = (idx) => setPForm((f) => ({ ...f, stages: f.stages.filter((_, i) => i !== idx) }));
  const updateChecklist = (si, ci, val) => setPForm((f) => ({ ...f, stages: f.stages.map((s, i) => i === si ? { ...s, checklist: s.checklist.map((c, j) => j === ci ? val : c) } : s) }));
  const addChecklist = (si) => setPForm((f) => ({ ...f, stages: f.stages.map((s, i) => i === si ? { ...s, checklist: [...s.checklist, ""] } : s) }));
  const removeChecklist = (si, ci) => setPForm((f) => ({ ...f, stages: f.stages.map((s, i) => i === si ? { ...s, checklist: s.checklist.filter((_, j) => j !== ci) } : s) }));
  const isSuggested = (name) => pForm.stages.some((s) => s.name === name);
  const toggleSuggested = (sug) => setPForm((f) => {
    const selected = new Set(f.stages.filter((s) => SUGGESTED_NAMES.has(s.name)).map((s) => s.name));
    selected.has(sug.name) ? selected.delete(sug.name) : selected.add(sug.name);
    const byName = Object.fromEntries(f.stages.map((s) => [s.name, s]));
    const suggested = SUGGESTED_STAGES.filter((x) => selected.has(x.name))
      .map((x) => byName[x.name] || { name: x.name, assignee: "", sop_text: "", checklist: x.checklist.length ? [...x.checklist] : [""] });
    const custom = f.stages.filter((s) => !SUGGESTED_NAMES.has(s.name));
    return { ...f, stages: [...suggested, ...custom] };
  });

  const createProject = async (e) => {
    e.preventDefault();
    if (!pForm.property_ref) { toast.error("Please choose a property"); return; }
    setBusy(true);
    try {
      const stages = pForm.stages
        .filter((s) => s.name.trim())
        .map((s) => ({ name: s.name.trim(), assignee: s.assignee.trim(), sop_text: s.sop_text.trim(), checklist: s.checklist.map((c) => c.trim()).filter(Boolean) }));
      await api.post("/projects", { type: pForm.type, property_ref: pForm.property_ref, contractor_ids: pForm.contractor_ids, stages });
      toast.success(stages.length ? "Project created — first stage task assigned" : "Project created");
      setNewProj(false); setPForm(initialForm());
      loadProjects();
    } catch { toast.error("Could not create project"); }
    finally { setBusy(false); }
  };

  const addContractor = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contractors", cForm);
      toast.success("Contractor added");
      setAddCon(false); setCForm({ name: "", phone: "", email: "", trade: "", areas: "" });
      loadContractors();
    } catch { toast.error("Could not add contractor"); }
    finally { setBusy(false); }
  };

  const delContractor = async (id) => { await api.delete(`/contractors/${id}`); loadContractors(); };

  const toggleCon = (id) => setPForm((f) => ({ ...f, contractor_ids: f.contractor_ids.includes(id) ? f.contractor_ids.filter((x) => x !== id) : [...f.contractor_ids, id] }));

  const conTerm = conQ.trim().toLowerCase();
  const shownContractors = conTerm.length >= 2
    ? contractors.filter((c) => `${c.name} ${c.trade} ${c.areas || ""}`.toLowerCase().includes(conTerm))
    : contractors;
  const showConList = conExpanded || conTerm.length >= 2;

  return (
    <Layout title="Projects" subtitle="Refurbishment Centre">
      <TeamInbox department="Refurbishment" />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button data-testid="contractors-toggle" onClick={() => setConExpanded((v) => !v)} className="flex items-center gap-1.5 group">
          <ChevronRight size={18} className={`text-slate-400 transition-transform ${conExpanded ? "rotate-90" : ""}`} />
          <h3 className="font-display text-lg font-semibold text-slate-900">Contractors</h3>
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{contractors.length}</span>
        </button>
        <div className="relative ml-auto w-48">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="contractor-search-input"
            value={conQ}
            onChange={(e) => setConQ(e.target.value)}
            placeholder="Search name or trade…"
            className="w-full pl-8 pr-7 py-1.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          {conQ && <button data-testid="contractor-search-clear" onClick={() => setConQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={14} /></button>}
        </div>
        <button data-testid="add-contractor-btn" onClick={() => setAddCon(true)} className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5"><Plus size={14} /> Add Contractor</button>
      </div>
      {showConList ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          {contractors.length === 0 && <p className="text-sm text-slate-400">No contractors yet — add your trusted trades.</p>}
          {contractors.length > 0 && shownContractors.length === 0 && <p className="text-sm text-slate-400">No contractors match your search.</p>}
          {shownContractors.map((c) => (
            <Card key={c.contractor_id} className="p-4 flex items-start justify-between" data-testid={`contractor-${c.contractor_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1"><p className="font-semibold text-slate-900 truncate">{c.name}</p>{c.trade && <Badge>{c.trade}</Badge>}</div>
                {c.phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone size={11} /> {c.phone}</p>}
                {c.email && <p className="text-xs text-slate-500 flex items-center gap-1"><Mail size={11} /> {c.email}</p>}
                {c.areas && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {c.areas}</p>}
              </div>
              <button data-testid={`del-contractor-${c.contractor_id}`} onClick={() => delContractor(c.contractor_id)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400 mb-8">{contractors.length} contractor{contractors.length === 1 ? "" : "s"} hidden — click “Contractors” to expand, or search above.</p>
      )}

      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-lg font-semibold text-slate-900">Projects</h3>
        <button data-testid="new-project-btn" onClick={() => setNewProj(true)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-1.5"><Plus size={15} /> New Project</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {projects.map((p) => (
          <Card key={p.project_id} className="p-6 fade-up" data-testid={`project-${p.project_id}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge>{p.type}</Badge>
                  <span className="text-xs text-blue-700">{p.property_address || p.property_ref}</span>
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900">{p.name}</h3>
                <p className="text-sm text-slate-500 mt-0.5">Stage: {p.stage}</p>
              </div>
              <div className="w-11 h-11 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center"><Hammer size={20} /></div>
            </div>

            <div className="mb-4">
              <label className="block text-xs text-slate-400 uppercase tracking-wider mb-1.5">Landlord-facing status</label>
              <select data-testid={`project-status-${p.project_id}`} value={p.landlord_status || "In Progress"}
                onChange={(e) => setStatus(p, e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                {LL_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>

            {p.budget > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Metric label="Budget" value={gbp(p.budget)} />
                <Metric label="Spent" value={gbp(p.spent)} accent="text-amber-600" />
                <Metric label="Remaining" value={gbp(p.budget - p.spent)} accent="text-emerald-600" />
              </div>
            )}

            {p.contractors.length > 0 && (
              <div className="pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Users size={13} /> Contractors</p>
                <div className="flex flex-wrap gap-2">
                  {p.contractors.map((c) => <span key={c} className="text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-md">{c}</span>)}
                </div>
              </div>
            )}
            <div className="pt-4 border-t border-slate-100">
              <button data-testid={`open-workflow-${p.project_id}`} onClick={() => setWfModal(p)}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 px-4 py-2.5 text-sm font-semibold transition-colors">
                <WorkflowIcon size={16} /> {p.workflow_id ? "Open workflow & stages" : "Add workflow & stages"}
              </button>
            </div>
            <ProjectQuotes projectId={p.project_id} contractors={p.contractors} onAccepted={loadProjects} />
            <ProjectThread projectId={p.project_id} senderRole="staff" />
          </Card>
        ))}
      </div>

      {wfModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setWfModal(null)}>
          <div data-testid="workflow-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-slate-900">Project workflow</h3>
              <button data-testid="close-workflow-modal" onClick={() => setWfModal(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <WorkflowTracker workflowId={wfModal.workflow_id || null} projectId={wfModal.project_id}
              onChanged={loadProjects} onDeleted={() => { setWfModal(null); loadProjects(); }} />
          </div>
        </div>
      )}

      {newProj && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setNewProj(false)}>
          <div data-testid="new-project-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5"><h3 className="font-display text-xl font-bold text-slate-900">New Project</h3><button onClick={() => setNewProj(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button></div>
            <form onSubmit={createProject} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Type</label>
                  <select data-testid="proj-type" value={pForm.type} onChange={(e) => setPForm({ ...pForm, type: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    {["Refurbishment", "Renovation", "Sale", "Letting", "Maintenance"].map((t) => <option key={t}>{t}</option>)}
                  </select></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Property</label>
                  <select data-testid="proj-property" value={pForm.property_ref} required onChange={(e) => setPForm({ ...pForm, property_ref: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    <option value="">Select a property…</option>
                    {properties.map((p) => <option key={p.property_id || p.reference} value={p.reference}>{p.address}</option>)}
                  </select></div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">Stages</label>
                  <p className="text-[11px] text-slate-400 mt-0.5">Each stage assigns a task; the next starts only once the previous is marked done.</p>
                </div>

                {(pForm.type === "Refurbishment" || pForm.type === "Renovation") && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50/70 p-3" data-testid="suggested-stages">
                    <p className="text-[11px] font-semibold text-blue-800 uppercase tracking-wider mb-2">Suggested stages — tick to add</p>
                    <div className="flex flex-wrap gap-1.5">
                      {SUGGESTED_STAGES.map((sug) => {
                        const on = isSuggested(sug.name);
                        return (
                          <button type="button" key={sug.name} data-testid={`suggest-${sug.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => toggleSuggested(sug)}
                            className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${on ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}>
                            {on ? "✓ " : "+ "}{sug.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {pForm.stages.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Tick a suggestion above or add a custom stage below.</p>}
                {pForm.stages.map((s, si) => (
                  <div key={si} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`proj-stage-${si}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500">Stage {si + 1}</span>
                      {pForm.stages.length > 1 && <button type="button" data-testid={`remove-stage-${si}`} onClick={() => removeStage(si)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}
                    </div>
                    <input data-testid={`stage-name-${si}`} value={s.name} onChange={(e) => updateStage(si, "name", e.target.value)} placeholder="Stage name — e.g. Strip out & first fix" className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    <select data-testid={`stage-assignee-${si}`} value={s.assignee} onChange={(e) => updateStage(si, "assignee", e.target.value)} className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600">
                      <option value="">Assign to (auto-creates their task)…</option>
                      {staff.map((m) => <option key={m.user_id} value={m.name}>{m.name}</option>)}
                      {s.assignee && !staff.some((m) => m.name === s.assignee) && <option value={s.assignee}>{s.assignee}</option>}
                    </select>
                    <textarea data-testid={`stage-sop-${si}`} rows={2} value={s.sop_text} onChange={(e) => updateStage(si, "sop_text", e.target.value)} placeholder="SOP — instructions for this stage (optional)" className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    <div className="space-y-1.5">
                      <label className="block text-[11px] font-medium text-slate-500">Checklist items</label>
                      {s.checklist.map((c, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <input data-testid={`stage-${si}-check-${ci}`} value={c} onChange={(e) => updateChecklist(si, ci, e.target.value)} placeholder="Add another item to this stage" className="flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                          {s.checklist.length > 1 && <button type="button" onClick={() => removeChecklist(si, ci)} className="text-slate-300 hover:text-red-500"><X size={13} /></button>}
                        </div>
                      ))}
                      <button type="button" data-testid={`add-check-${si}`} onClick={() => addChecklist(si)} className="text-xs font-medium text-blue-700 hover:text-blue-800">+ Add item</button>
                    </div>
                  </div>
                ))}
                <button type="button" data-testid="add-stage-btn" onClick={addStage} className="w-full border border-dashed border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-700 rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-1.5"><Plus size={14} /> Add stage</button>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Request quotes from contractors</label>
                {contractors.length === 0 ? <p className="text-xs text-slate-400">No contractors yet — add some first.</p> : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {contractors.map((c) => (
                      <label key={c.contractor_id} data-testid={`proj-con-${c.contractor_id}`} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" checked={pForm.contractor_ids.includes(c.contractor_id)} onChange={() => toggleCon(c.contractor_id)} className="rounded" />
                        {c.name} {c.trade && <span className="text-xs text-slate-400">· {c.trade}</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <button type="submit" data-testid="create-project-btn" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : null} Create Project</button>
            </form>
          </div>
        </div>
      )}

      {addCon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setAddCon(false)}>
          <div data-testid="add-contractor-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5"><h3 className="font-display text-xl font-bold text-slate-900">Add Contractor</h3><button onClick={() => setAddCon(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button></div>
            <form onSubmit={addContractor} className="space-y-3">
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
                <input data-testid="con-name" value={cForm.name} required onChange={(e) => setCForm({ ...cForm, name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Type of work / trade</label>
                <input data-testid="con-trade" value={cForm.trade} onChange={(e) => setCForm({ ...cForm, trade: e.target.value })} placeholder="e.g. Electrician" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Phone / number</label>
                  <input data-testid="con-phone" value={cForm.phone} onChange={(e) => setCForm({ ...cForm, phone: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                  <input data-testid="con-email" type="email" value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Areas covered</label>
                <input data-testid="con-areas" value={cForm.areas} onChange={(e) => setCForm({ ...cForm, areas: e.target.value })} placeholder="e.g. Bristol, Bath, BS postcodes" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <button type="submit" data-testid="save-contractor-btn" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : null} Add Contractor</button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Metric({ label, value, accent = "text-slate-900" }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`font-display font-bold tabular-nums text-sm mt-0.5 ${accent}`}>{value}</p>
    </div>
  );
}


const QUOTE_BADGE = { Submitted: "bg-blue-50 text-blue-700", Accepted: "bg-emerald-50 text-emerald-700", Declined: "bg-slate-100 text-slate-500 line-through" };

function ProjectQuotes({ projectId, contractors, onAccepted }) {
  const [quotes, setQuotes] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ amount: "", notes: "", contractor_name: (contractors && contractors[0]) || "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/projects/${projectId}/quotes`).then(({ data }) => setQuotes(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const accept = async (q) => {
    setBusy(true);
    try { await api.post(`/projects/${projectId}/quotes/${q.quote_id}/accept`); toast.success(`Accepted ${q.contractor_name}'s quote`); await load(); onAccepted && onAccepted(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not accept"); } finally { setBusy(false); }
  };
  const decline = async (q) => {
    setBusy(true);
    try { await api.post(`/projects/${projectId}/quotes/${q.quote_id}/decline`); toast.success(`Declined ${q.contractor_name}'s quote`); await load(); onAccepted && onAccepted(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not decline"); } finally { setBusy(false); }
  };
  const reopen = async (q) => {
    setBusy(true);
    try { await api.post(`/projects/${projectId}/quotes/${q.quote_id}/reopen`); await load(); onAccepted && onAccepted(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); } finally { setBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/quotes`, { amount: parseFloat(form.amount) || 0, notes: form.notes, contractor_name: form.contractor_name });
      toast.success("Quote recorded"); setForm({ amount: "", notes: "", contractor_name: (contractors && contractors[0]) || "" }); setAdding(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add quote"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`project-quotes-${projectId}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5"><FileText size={13} /> Contractor quotes</p>
        <button onClick={() => setAdding((v) => !v)} data-testid={`add-quote-${projectId}`} className="text-xs font-medium text-blue-700 hover:text-blue-800 flex items-center gap-1"><Plus size={12} /> Add quote</button>
      </div>

      {quotes.length === 0 && !adding && <p className="text-xs text-slate-400">No quotes yet.</p>}

      <div className="space-y-2">
        {quotes.map((q) => (
          <div key={q.quote_id} data-testid={`quote-${q.quote_id}`} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800 tabular-nums">{gbp(q.amount)}</span>
                <span className="text-xs text-slate-500 truncate">{q.contractor_name}</span>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${QUOTE_BADGE[q.status]}`}>{q.status}</span>
              </div>
              {q.notes && <p className="text-xs text-slate-400 truncate">{q.notes}</p>}
            </div>
            {q.status === "Submitted" && (
              <div className="shrink-0 flex items-center gap-1.5">
                <button onClick={() => accept(q)} disabled={busy} data-testid={`accept-quote-${q.quote_id}`}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60">
                  <Check size={13} /> Accept
                </button>
                <button onClick={() => decline(q)} disabled={busy} data-testid={`decline-quote-${q.quote_id}`}
                  className="border border-slate-300 text-slate-600 hover:bg-slate-100 rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60">
                  Decline
                </button>
              </div>
            )}
            {(q.status === "Accepted" || q.status === "Declined") && (
              <button onClick={() => reopen(q)} disabled={busy} data-testid={`undo-quote-${q.quote_id}`}
                className="shrink-0 text-xs font-medium text-slate-400 hover:text-blue-700 disabled:opacity-60">
                Undo
              </button>
            )}
          </div>
        ))}
      </div>

      {adding && (
        <form onSubmit={submit} className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-2">
          <div className="flex-1 min-w-[110px]">
            <label className="block text-[10px] text-slate-400 uppercase mb-0.5">Amount (£)</label>
            <input data-testid={`quote-amount-${projectId}`} type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-[10px] text-slate-400 uppercase mb-0.5">Contractor</label>
            {contractors && contractors.length > 0
              ? <select value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">{contractors.map((c) => <option key={c}>{c}</option>)}</select>
              : <input value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} placeholder="Contractor name" className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />}
          </div>
          <div className="flex-[2] min-w-[140px]">
            <label className="block text-[10px] text-slate-400 uppercase mb-0.5">Notes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Scope / breakdown" className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <button type="submit" disabled={busy} data-testid={`save-quote-${projectId}`} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save</button>
        </form>
      )}
    </div>
  );
}
